import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { authAPI, formatApiError } from "../lib/api";
import { saveRefreshToken, loadRefreshToken, clearRefreshToken } from "../lib/tokenStore";

const AuthContext = createContext(null);

const ORIGINAL_TOKEN_KEY = "token_original";
const TOKEN_KEY = "token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Proactively refresh on cold-start. iOS PWA standalone mode is more likely
// to lose the httpOnly cookie than to lose IndexedDB, so we prefer hitting
// /auth/refresh directly (with the IndexedDB-backed refresh token as a body
// fallback) BEFORE the very first /auth/me request. Avoids the "first call
// 401 race" that bites iOS the hardest.
async function proactiveRefresh() {
  const stored = await loadRefreshToken();
  const body = stored ? { refresh_token: stored } : {};
  // axios.post not `api` instance — we want raw call without the 401
  // interceptor (which would itself try to refresh and loop).
  const { data } = await axios.post(`${API}/auth/refresh`, body, { withCredentials: true });
  if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
  if (data?.refresh_token) saveRefreshToken(data.refresh_token);
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Cold-start: try a proactive refresh first IF we have any stored
    // credential to refresh against. If neither cookie nor IndexedDB token
    // is available, skip — let /auth/me decide (still works if the cookie
    // is fresh from the same browser session).
    //
    // EXCEPTION: when we're impersonating another user (ORIGINAL_TOKEN_KEY
    // set), we MUST NOT proactively refresh. The refresh cookie/IndexedDB
    // still belong to the admin, so a refresh here would mint a fresh
    // admin token and clobber the 30-minute impersonation token we just
    // stashed in `token`. (Regression caught 2026-05-27 — "Preview as
    // user" returned a success toast but the UI never switched away from
    // the admin's view.)
    const isImpersonating = !!localStorage.getItem(ORIGINAL_TOKEN_KEY);
    if (!isImpersonating) {
      try {
        const stored = await loadRefreshToken();
        const hasAccessToken = !!localStorage.getItem(TOKEN_KEY);
        if (stored || !hasAccessToken) {
          try { await proactiveRefresh(); } catch { /* ignored */ }
        }
      } catch { /* ignored */ }
    }
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Auth check failed:", e);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await authAPI.login({ email, password });
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      if (data.refresh_token) saveRefreshToken(data.refresh_token);
      // Clear any leftover impersonation state from a previous session
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await authAPI.register({ email, password, name });
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      if (data.refresh_token) saveRefreshToken(data.refresh_token);
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Best-effort — local state is cleared regardless of server response
      console.warn("Logout request failed:", e);
    }
    localStorage.removeItem(TOKEN_KEY);
    clearRefreshToken();
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    setUser(false);
  };

  const impersonate = async (userId) => {
    try {
      const { data } = await authAPI.impersonate(userId);
      // Snapshot the current (admin) token so we can restore it on Exit
      // Preview. ORIGINAL_TOKEN_KEY being set is ALSO the signal the rest
      // of the auth layer uses to know "don't proactively refresh — the
      // refresh credentials still belong to the admin and would clobber
      // the impersonation access token."
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (currentToken) localStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken);
      if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
      // Use the user object the impersonate endpoint already returned.
      // Calling checkAuth() here would re-fetch /auth/me which is fine in
      // theory, but the previous regression went through proactiveRefresh
      // first. Setting the user directly is both faster and removes a
      // potential foot-gun if anything else in checkAuth depends on the
      // refresh-token state.
      //
      // We also fold the impersonator metadata into the user object so
      // Layout's "Previewing as X — signed in as Y" banner + the Exit
      // Preview button render correctly. (The /auth/me endpoint surfaces
      // these from the JWT; we don't want to wait for the next /auth/me
      // call before the banner shows up.)
      if (data.user) {
        const merged = { ...data.user };
        if (data.impersonator) {
          merged.impersonator_id = data.impersonator.id;
          merged.impersonator_name = data.impersonator.name;
        }
        setUser(merged);
      } else {
        await checkAuth();
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const exitImpersonation = async () => {
    const original = localStorage.getItem(ORIGINAL_TOKEN_KEY);
    if (original) {
      localStorage.setItem(TOKEN_KEY, original);
      localStorage.removeItem(ORIGINAL_TOKEN_KEY);
      await checkAuth();
    } else {
      // Fallback: nothing snapshotted, just clear and force re-login
      await logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, impersonate, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
